/**
 * 顾客建档 / 编辑 —— 同一个表单两用。
 *
 * 拆成两个组件会让「新建时要填什么」和「编辑时能改什么」慢慢漂移，
 * 最后总有一边少个字段。传 customerId 就是编辑，不传就是新建。
 *
 * 必填只有姓名和电话，其余全部可后补 —— 接电话或顾客站在柜台前时，
 * 问不完那一长串。
 */

import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Info } from 'lucide-react'
import { api, type Lang } from '../lib/api'
import { Button, Drawer, Field, Input, Segmented, Textarea, useToast } from '../components/ui'

interface Props {
  open: boolean
  onClose: () => void
  /** 传了就是编辑模式 */
  customerId?: string
  onSaved?: (id: string, name: string) => void
  initialName?: string
  initialPhone?: string
}

interface Loaded {
  fullName: string
  phoneE164: string
  altPhoneE164: string | null
  altPhoneIsFamily: boolean
  gender: string | null
  birthYear: number | null
  language: string
  addressLine: string | null
  area: string | null
  healthNotes: string | null
  notes: string | null
}

export function CustomerDrawer({
  open,
  onClose,
  customerId,
  onSaved,
  initialName,
  initialPhone,
}: Props) {
  const qc = useQueryClient()
  const toast = useToast()
  const editing = Boolean(customerId)

  const [fullName, setFullName] = useState('')
  const [phone, setPhone] = useState('')
  const [altPhone, setAltPhone] = useState('')
  const [altIsFamily, setAltIsFamily] = useState(false)
  const [gender, setGender] = useState<'M' | 'F' | null>(null)
  const [birthYear, setBirthYear] = useState('')
  const [language, setLanguage] = useState<Lang>('ms')
  const [address, setAddress] = useState('')
  const [area, setArea] = useState('')
  const [healthNotes, setHealthNotes] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const existing = useQuery({
    queryKey: ['customer', customerId],
    queryFn: () => api.get<{ customer: Loaded }>(`/api/customers/${customerId}`),
    enabled: open && editing,
  })

  // 新建：用搜索框里输的内容预填。编辑：等资料拉回来再填。
  useEffect(() => {
    if (!open) return
    if (editing) {
      const c = existing.data?.customer
      if (!c) return
      setFullName(c.fullName)
      setPhone(c.phoneE164)
      setAltPhone(c.altPhoneE164 ?? '')
      setAltIsFamily(c.altPhoneIsFamily)
      setGender((c.gender as 'M' | 'F' | null) ?? null)
      setBirthYear(c.birthYear ? String(c.birthYear) : '')
      setLanguage((c.language as Lang) ?? 'ms')
      setAddress(c.addressLine ?? '')
      setArea(c.area ?? '')
      setHealthNotes(c.healthNotes ?? '')
      setNotes(c.notes ?? '')
    } else {
      setFullName(initialName ?? '')
      setPhone(initialPhone ?? '')
      setAltPhone('')
      setAltIsFamily(false)
      setGender(null)
      setBirthYear('')
      setLanguage('ms')
      setAddress('')
      setArea('')
      setHealthNotes('')
      setNotes('')
    }
    setError(null)
  }, [open, editing, existing.data, initialName, initialPhone])

  const save = useMutation({
    mutationFn: () => {
      const body = {
        fullName: fullName.trim(),
        phone: phone.trim(),
        altPhone: altPhone.trim() || null,
        altPhoneIsFamily: altIsFamily,
        gender,
        birthYear: birthYear ? Number(birthYear) : null,
        language,
        addressLine: address.trim() || null,
        area: area.trim() || null,
        healthNotes: healthNotes.trim() || null,
        notes: notes.trim() || null,
      }
      return editing
        ? api.patch<{ customer: { id: string; fullName: string } }>(
            `/api/customers/${customerId}`,
            body,
          )
        : api.post<{
            customer: { id: string; fullName: string }
            duplicateWarnings: Array<{ id: string; fullName: string }>
          }>('/api/customers', body)
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['customer-list'] })
      void qc.invalidateQueries({ queryKey: ['search'] })
      if (customerId) void qc.invalidateQueries({ queryKey: ['customer', customerId] })

      const dupes = (r as { duplicateWarnings?: unknown[] }).duplicateWarnings
      if (!editing && dupes && dupes.length > 0) {
        toast.show(`已建档。注意：这个号码下已有 ${dupes.length} 位顾客`)
      } else {
        toast.show(editing ? '已保存' : '已建档')
      }
      onSaved?.(r.customer.id, r.customer.fullName)
      onClose()
    },
    onError: (e) => setError((e as Error).message),
  })

  const canSubmit = fullName.trim().length > 0 && phone.trim().length >= 8
  const loading = editing && existing.isLoading

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={editing ? '编辑顾客资料' : '新建顾客'}
      footer={
        <>
          <Button size="lg" onClick={onClose}>
            取消
          </Button>
          <Button
            size="lg"
            variant="primary"
            disabled={!canSubmit || loading}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            {editing ? '保存' : '建档'}
          </Button>
        </>
      }
    >
      {loading ? (
        <p className="py-8 text-center text-[17px] text-ink-3">正在加载资料…</p>
      ) : (
        <div className="flex flex-col gap-5">
          {error && (
            <div className="rounded-md border border-bad-line bg-bad-bg px-4 py-3 text-[16px] text-bad">
              {error}
            </div>
          )}

          <Field label="姓名" required hint="马来名直接整串填，不用拆姓和名">
            <Input
              autoFocus={!editing}
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder="Siti binti Hassan"
            />
          </Field>

          <Field label="电话" required hint="填 0 开头即可，系统自动转成 +60">
            <Input
              inputMode="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="0138908890"
            />
          </Field>

          <Field label="第二电话" hint="老人常常没有手机，这里填子女的号码">
            <Input
              inputMode="tel"
              value={altPhone}
              onChange={(e) => setAltPhone(e.target.value)}
              placeholder="选填"
            />
          </Field>

          {altPhone.trim() && (
            <label className="flex min-h-11 cursor-pointer items-center gap-3 text-[17px]">
              <input
                type="checkbox"
                checked={altIsFamily}
                onChange={(e) => setAltIsFamily(e.target.checked)}
                className="size-5.5 accent-brand-600"
              />
              这个号码属于家属
            </label>
          )}

          {altIsFamily && (
            <p className="flex gap-2 rounded-md bg-info-bg px-4 py-3 text-[15px] text-info">
              <Info size={18} className="mt-0.5 shrink-0" />
              提醒会发到这个号码，文案自动改成第三人称，家属才看得懂。
            </p>
          )}

          <Field label="提醒语言" hint="决定所有提醒与顾客链接页的语言">
            <Segmented
              value={language}
              onChange={setLanguage}
              ariaLabel="提醒语言"
              options={[
                { value: 'ms', label: 'Melayu' },
                { value: 'en', label: 'English' },
                { value: 'zh', label: '中文' },
              ]}
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="性别">
              <Segmented
                value={gender}
                onChange={setGender}
                ariaLabel="性别"
                options={[
                  { value: 'M', label: '男' },
                  { value: 'F', label: '女' },
                ]}
              />
            </Field>
            <Field label="出生年份" hint="只记年份">
              <Input
                inputMode="numeric"
                maxLength={4}
                value={birthYear}
                onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, ''))}
                placeholder="1964"
                className="tnum"
              />
            </Field>
          </div>

          <Field label="地区">
            <Input value={area} onChange={(e) => setArea(e.target.value)} placeholder="Kota Bharu" />
          </Field>

          <Field label="地址">
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="选填"
            />
          </Field>

          <Field label="健康备注" hint="填写前请先问顾客是否同意记录。只填与做假牙相关的内容。">
            <Textarea
              value={healthNotes}
              onChange={(e) => setHealthNotes(e.target.value)}
              placeholder="例：糖尿病，愈合较慢"
              maxLength={1000}
            />
          </Field>

          <Field label="备注">
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </Field>
        </div>
      )}
    </Drawer>
  )
}

/** 保留旧名字，避免把所有调用点都改一遍 */
export function NewCustomerDrawer(props: Omit<Props, 'customerId'> & { onCreated?: Props['onSaved'] }) {
  const { onCreated, ...rest } = props
  return <CustomerDrawer {...rest} onSaved={onCreated} />
}
